import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

/**
 * Theme:
 *  - 'auto'  → follow the OS's prefers-color-scheme
 *  - 'light' → always light
 *  - 'dark'  → always dark
 *
 * Stored in localStorage under 'connectify-theme'. The actual applied theme
 * (resolved) is exposed as `effectiveTheme` so consumers can read 'light' or
 * 'dark' even when mode is 'auto'.
 */
const STORAGE_KEY = 'connectify-theme';
const VALID_MODES = ['auto', 'light', 'dark'];

const ThemeContext = createContext({
  mode: 'auto',
  effectiveTheme: 'light',
  setMode: () => {},
  cycleMode: () => {},
});

const getSystemTheme = () =>
  (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches)
    ? 'dark'
    : 'light';

const applyHtmlClass = (effective) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (effective === 'dark') {
    root.classList.add('dark');
    root.style.colorScheme = 'dark';
  } else {
    root.classList.remove('dark');
    root.style.colorScheme = 'light';
  }
};

export const ThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState(() => {
    if (typeof window === 'undefined') return 'auto';
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return VALID_MODES.includes(stored) ? stored : 'auto';
  });
  const [effectiveTheme, setEffectiveTheme] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark') return 'dark';
    if (stored === 'light') return 'light';
    return getSystemTheme();
  });

  // Recompute effective theme whenever `mode` changes
  useEffect(() => {
    const resolved = mode === 'auto' ? getSystemTheme() : mode;
    setEffectiveTheme(resolved);
    applyHtmlClass(resolved);
  }, [mode]);

  // Listen to OS preference changes (only relevant when mode === 'auto')
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      if (mode === 'auto') {
        const resolved = e.matches ? 'dark' : 'light';
        setEffectiveTheme(resolved);
        applyHtmlClass(resolved);
      }
    };
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else if (mq.addListener) mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else if (mq.removeListener) mq.removeListener(handler);
    };
  }, [mode]);

  const setMode = useCallback((newMode) => {
    const safe = VALID_MODES.includes(newMode) ? newMode : 'auto';
    setModeState(safe);
    try {
      window.localStorage.setItem(STORAGE_KEY, safe);
    } catch (_) { /* ignore */ }
  }, []);

  const cycleMode = useCallback(() => {
    setMode(mode === 'auto' ? 'light' : mode === 'light' ? 'dark' : 'auto');
  }, [mode, setMode]);

  return (
    <ThemeContext.Provider value={{ mode, effectiveTheme, setMode, cycleMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
